import getAuthHeaders from "./authHeaders";
import { DEFAULT_MODE } from "./modes";

// A live run reports itself every 15 seconds, in every phase. Four missed
// reports mean the connection or the run is gone: stop waiting on it.
export const STREAM_INACTIVITY_TIMEOUT_MS = 60 * 1000;

export class StreamInactivityError extends Error {
  constructor() {
    super('The answer stream went quiet');
    this.name = 'StreamInactivityError';
  }
}

type SendMessageCallbacks = {
  onStatus?: (status: string) => void;
  onDelta?: (text: string) => void;
  onHeartbeat?: () => void;
  onDone?: (messages: any[]) => void;
};

function buildRequestBody(
  text:string,
  chatId:string,
  mode?:string,
  uploadedFiles?: {id: string, title: string}[],
  useUserProfile?: boolean
) {
  const body: Record<string, any> = {
    text,
    chatId,
    mode: mode || DEFAULT_MODE,
    useUserProfile: !!useUserProfile,
  };

  if (uploadedFiles && uploadedFiles.length > 0) {
    body.uploadedFiles = uploadedFiles;
  }

  return body;
}

async function fetchSendMessageJson(
  text:string,
  chatId:string,
  apiAddress:string,
  mode?:string,
  uploadedFiles?: {id: string, title: string}[],
  useUserProfile?: boolean
) {
  const link = `${apiAddress}/chat`;

  const response = await fetch(link, {
    method: 'POST',
    headers: await getAuthHeaders({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(buildRequestBody(text, chatId, mode, uploadedFiles, useUserProfile)),
  });

  const data = await response.json();
  return { ...data, _status: response.status };
}

function parseServerSentEvent(rawEvent: string) {
  const lines = rawEvent.split(/\r?\n/);
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  const dataText = dataLines.join('\n');
  return {
    event,
    data: dataText ? JSON.parse(dataText) : {},
  };
}

export default async function fetchSendMessage(
  text:string,
  chatId:string,
  apiAddress:string,
  mode?:string,
  uploadedFiles?: {id: string, title: string}[],
  useUserProfile?: boolean,
  callbacks: SendMessageCallbacks = {}
) {
  const link = `${apiAddress}/chat_stream`;
  const response = await fetch(link, {
    method: 'POST',
    headers: await getAuthHeaders({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(buildRequestBody(text, chatId, mode, uploadedFiles, useUserProfile)),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok || !response.body || !contentType.includes('text/event-stream')) {
    if (response.status === 404 || response.status === 405) {
      return fetchSendMessageJson(text, chatId, apiAddress, mode, uploadedFiles, useUserProfile);
    }

    const data = await response.json();
    return { ...data, _status: response.status };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalData: any = {};
  let streamError: any = null;
  let sawEnd = false;

  const readWithTimeout = () =>
    new Promise<ReadableStreamReadResult<Uint8Array>>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        reader.cancel().catch(() => undefined);
        reject(new StreamInactivityError());
      }, STREAM_INACTIVITY_TIMEOUT_MS);
      reader.read().then(
        (result) => {
          window.clearTimeout(timer);
          resolve(result);
        },
        (error) => {
          window.clearTimeout(timer);
          reject(error);
        }
      );
    });

  while (true) {
    const { value, done } = await readWithTimeout();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const rawEvent = buffer.slice(0, boundary).trim();
      buffer = buffer.slice(boundary + 2);

      if (rawEvent) {
        const { event, data } = parseServerSentEvent(rawEvent);

        if (event === 'status' && data.status) {
          callbacks.onStatus?.(data.status);
        } else if (event === 'delta' && data.text) {
          callbacks.onDelta?.(data.text);
        } else if (event === 'heartbeat') {
          callbacks.onHeartbeat?.();
        } else if (event === 'done') {
          sawEnd = true;
          finalData = data;
          callbacks.onDone?.(data.messages || []);
        } else if (event === 'error') {
          sawEnd = true;
          streamError = data;
        }
      }

      boundary = buffer.indexOf('\n\n');
    }
  }

  if (streamError) {
    return { ...streamError, _status: streamError._status || 500 };
  }

  if (!sawEnd) {
    // The connection closed before the run reported its end. The server keeps
    // the turn, so the caller should keep what arrived and let the chat's own
    // status settle it.
    return { ...finalData, _status: response.status, _streamLost: true };
  }

  return { ...finalData, _status: response.status };

}
